import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { push } from 'redux-first-history';
import { useContactQueryParams, useContactsCount, useEntityRuleForFiltration } from '@/components/Contacts/Group/hooks';
import { ContactsPaths } from '@/services/dictionaries/Paths';
import { calculateActivePage, calculateOffset } from '@/services/tools/handleUrlParams';
import { ContactsDeleteStart, getContactsStore } from '@/store/contacts/actions';
import { ShowDialog, UpdateDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { ContactTableContext } from '../../context';
import { HardDeleteDialog } from '../../components/HardDeleteDialog';
import { DELETE_CONFIRMATION_TEXT } from '../../components/HardDeleteDialog/constants';

export const useDeleteContactsFromList = () => {
    const { t } = useTranslation();
    const { selectedRows, selectedAll, setSelectedRows, setSelectedAll } = useContext(ContactTableContext);
    const { queryStatuses, queryOrderBy, querySearch, queryPage, pushParamToUrl, queryPerPage } =
        useContactQueryParams();
    const contactsCount = useContactsCount();
    const dispatch = useDispatch();
    const { listname } = useParams<{ listname: string }>();
    const { entityRule, filtrationRuleWithQuery } = useEntityRuleForFiltration();
    const isList = !!listname;
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const contacts = useSelector(getContactsStore);

    const deleteAndGetContacts = (emails: string[], countAfterDelete: number): void => {
        const rule = filtrationRuleWithQuery;
        const statuses = selectedAll ? null : queryStatuses;
        const limit = queryPerPage;
        const offset = limit * (queryPage - 1);
        const activePage = countAfterDelete > limit ? calculateActivePage(offset, limit, countAfterDelete) : 1;
        const offsetAfterRemove = calculateOffset(activePage, limit, countAfterDelete);
        const selectedAllRuleParam = isList ? entityRule : null;

        const searchParams = {
            search: querySearch,
            orderBy: queryOrderBy,
            limit: queryPerPage,
        };

        const selectedAllDeleteParams = {
            deleteParams: {
                rule,
            },
            loadParams: {
                ...searchParams,
                rule: selectedAllRuleParam,
                statuses: null,
                offset: 0,
            },
        };
        const deleteParams = {
            deleteParams: {
                emails,
            },
            loadParams: {
                ...searchParams,
                rule: selectedAllRuleParam,
                statuses,
                offset: offsetAfterRemove,
            },
        };

        if (emails) {
            dispatch(ContactsDeleteStart(selectedAll ? selectedAllDeleteParams : deleteParams));
        } else {
            dispatch(ContactsDeleteStart({ deleteParams: { rule } }));
            dispatch(push(isList ? ContactsPaths.Lists : ContactsPaths.Segments));
        }

        setSelectedAll(false);
        setSelectedRows([]);
        selectedAll && pushParamToUrl({ statuses: null });
    };

    const changeDeleteConfirmationText = (text: string): void => {
        setDeleteConfirmationText(text);
        dispatch(UpdateDialog({ isDisabled: text !== DELETE_CONFIRMATION_TEXT }));
    };

    const deleteContactsFromList = (email?: string): void => {
        const emailsToDelete = !!email ? [email] : selectedRows;
        const emailsToDeleteLength = emailsToDelete.length > 1 ? emailsToDelete.length : '' ;
        const numberOfItemsText = selectedAll ? contactsCount : emailsToDeleteLength;
        const numberOfItems = selectedAll ? contactsCount : emailsToDelete.length;
        const countAfterDelete = contactsCount - numberOfItems;
        
        const isContactFromPaidList = selectedAll 
            ? contacts.collection.some(contact => contact.IsOnPaidList)
            : emailsToDelete.some(email => 
                contacts.collection.find(contact => contact.Email === email)?.IsOnPaidList
            );

        const params: ShowDialogParams = {
            title: `${t('delete')} ${numberOfItemsText} ${
                emailsToDelete.length > 1 ? t('contacts').toLowerCase() : t('contact').toLowerCase()
            }`,
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => {
                deleteAndGetContacts(emailsToDelete, countAfterDelete);
            },
            cancelLabel: t('cancel'),
            onClickCancel: (): void => {
                setDeleteConfirmationText('');
            },
            isDisabled: true,
            body: (
                <HardDeleteDialog 
                    text={emailsToDelete.length > 1 ? t('deletecontactsmsg') : t('deletecontactmsg')}
                    deleteConfirmationText={deleteConfirmationText}
                    changeDeleteConfirmationText={changeDeleteConfirmationText}
                    isContactFromPaidList={isContactFromPaidList}
                />
            ),
        };
        dispatch(ShowDialog(params));
    };

    return { deleteContactsFromList };
};
